<template>
  <div v-if="dogNotFound" class="mt-5">
    <v-alert type="error">Dog not found.</v-alert>
    <router-link to="/admin/search/dogs">Back to dog search</router-link>
  </div>
  <v-sheet
    v-else
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
  >
  <v-dialog v-model="showReportDialog">
      <report-viewer
        :reportName="Report_Name"
        :dataUrl="Report_URL"
        ref="reportViewer"
        :printOnly="true"
      />
    </v-dialog> 
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate color="primary" />
    </v-overlay>
    <div v-if="!dogId || dogInfo.dogId" class="profile-content">
      <v-row>
        <v-col cols="8">
            <h1>{{ dogTitle }}</h1>
            <h2 v-if="hasExistingDog">{{ registeredName }}</h2>
            <h4>{{dogInfo.dogPrefix}}</h4>
            <h4>{{dogInfo.dogSuffix}}</h4>
        </v-col>
        <v-col v-if="hasExistingDog" cols="4">
          <v-btn class="ma-1" color="secondary" v-on:click="showNewPedigreeDialog()">Pedigree</v-btn>&nbsp;
          <v-btn class="ma-1" color="secondary" v-on:click="showRegCardDialog()">Registration Card</v-btn>&nbsp;<br/>
          <v-btn class="ma-1" color="secondary" v-on:click="showProgenyAdminDialog()">Progeny Report - Admin</v-btn>&nbsp;
          <v-btn class="ma-1" color="secondary" v-on:click="showProgenyDialog()">Progeny Report - Member</v-btn>&nbsp;
        </v-col></v-row>
      <v-divider class="my-3"></v-divider>
      <v-tabs v-if="hasExistingDog" v-model="tab">
        <v-tab key="registration"> Registration </v-tab>
        <v-tab key="showResults"> Show Results </v-tab>
        <v-tab key="titles"> Titles & Name</v-tab>
      </v-tabs>
      <v-tabs-items v-if="hasExistingDog" v-model="tab">
        <v-tab-item key="registration">
          <registration :dogId="dogId" @saved="loadDog" />
        </v-tab-item>
        <v-tab-item key="showResults">
          <show-results :dogId="dogId" />
        </v-tab-item>
        <v-tab-item key="titles">
          <titles :dogId="dogId" :manualPrefix="dogInfo.dogPrefixManual" :manualSuffix="dogInfo.dogSuffixManual" :dogFullName="dogInfo.dogFullName" 
            v-bind:dogNamePrefix.sync="dogInfo.dogPrefix"
            v-bind:dogNameSuffix.sync="dogInfo.dogSuffix"
          /> 
        </v-tab-item>
      </v-tabs-items>
      <registration v-else :dogId="dogId" @saved="loadDog" />
    </div>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import Registration from "@/components/Admin/Details/Dogs/Registration.vue";
import ShowResults from "@/components/Admin/Details/Dogs/ShowResults.vue";
import Titles from "@/components/Admin/Details/Dogs/Titles.vue";
import ReportViewer from "@/components/Admin/Reports/ReportViewer.vue";

import dogService from "@/services/admin/dogService";
import { DogInfo } from "@/types/admin/Dog";

export default Vue.extend({
  name: "DogDetails",
  data: function () {
    return {
      dogInfo: {} as Partial<DogInfo>,
      dogNotFound: false,
      loading: false,
      loadSequence: 0,
      Report_Name: 'Asca.Api.Reports.rpt3210_Dog_Reg_Certificate.rdlx',
      Report_URL: '`/reports/registration/${dogId}`',
      showReportDialog: false,
      tab: null,
    };
  },  
  props: {
    dogId: { type: String },
  },
  components: {
    Registration,
    ShowResults,
    Titles,
    ReportViewer
  },
  computed: {
    dogTitle: function (): string {
      return this.dogId || "New Dog Registration";
    },
    registeredName: function (): string {
      const callName = this.dogInfo.callName?.trim();
      return callName ? `${this.dogInfo.name} ("${callName}")` : this.dogInfo.name || "";
    },
    hasExistingDog: function (): boolean {
      return !!this.dogId && !!this.dogInfo.dogId;
    },
  },
  methods: {
    showRegCardDialog: function (){   
      if (!this.hasExistingDog) return;
      this.Report_Name = "Asca.Api.Reports.rpt3210_Dog_Reg_Certificate.rdlx";
      this.Report_URL = `/reports/registration/${encodeURIComponent(this.dogId.trim())}`;
      this.showReportDialog = true;
    },
    showNewPedigreeDialog: function (){
      if (!this.hasExistingDog) return;
      this.Report_Name = "Asca.Api.Reports.rpt3240_Dog_Pedigree_Report_New2.rdlx";
      this.Report_URL = `/reports/pedigree/${encodeURIComponent(this.dogId.trim())}`;
      this.showReportDialog = true;
    },
    showProgenyAdminDialog: function (){
      if (!this.hasExistingDog) return;
      this.Report_Name = "Asca.Api.Reports.rpt3250_Dog_Progeny_Report.rdlx";
      this.Report_URL = `/admin/reports/progeny/${encodeURIComponent(this.dogId.trim())}`;
      this.showReportDialog = true;
    },    
    showProgenyDialog: function (){
      if (!this.hasExistingDog) return;
      this.Report_Name = "Asca.Api.Reports.rpt3250_Dog_Progeny_Report.rdlx";
      this.Report_URL = `/reports/progeny/${encodeURIComponent(this.dogId.trim())}`;
      this.showReportDialog = true;
    },
    loadDog: function () {
      const loadSequence = ++this.loadSequence;
      if (!this.dogId) {
        this.loading = false;
        this.dogNotFound = false;
        this.dogInfo = {};
        return;
      }

      this.loading = true;
      this.dogNotFound = false;
      this.dogInfo = {};
      dogService
        .getDogById(this.dogId)
        .then((res) => {
          if (loadSequence !== this.loadSequence) return;
          if (!res || !res.dogId) {
            this.dogNotFound = true;
            return;
          }
          this.dogInfo = res;
        })
        .catch((error) => {
          if (loadSequence !== this.loadSequence) return;
          if (error?.response?.status === 404) {
            this.dogNotFound = true;
          } else {
            this.$handleError(error);
          }
        })
        .finally(() => {
          if (loadSequence === this.loadSequence) this.loading = false;
        });
    },
  },
  created: function () {
    this.loadDog();
  },
  watch: {
    dogId: function () {
      this.loadDog();
    },
  },
  beforeDestroy: function () {
    this.loadSequence++;
  },
});
</script>
